import {
  ArrowLeftRight,
  Bell,
  BriefcaseBusiness,
  Gauge,
  Gem,
  Globe,
  HeartHandshake,
  Layers,
  ListOrdered,
  MailWarning,
  MessagesSquare,
  SlidersHorizontal,
  Tags,
  UserCog,
  UserSquare,
  UsersRound,
} from "lucide-react";
import { useAppSettings } from "@/hooks/use-app-settings";
import { SettingsSubNav, type SettingsSubNavTab } from "./SettingsSubNav";

const configTabs: readonly SettingsSubNavTab[] = [
  // What a skill or competency is filed under — many-to-many, covering both
  // (formerly two separate lists: a single-pick "Skill Category" and a
  // competency-only "Competency Theme"; merged 2026-10-14, see the page).
  {
    label: "Categories",
    href: "/dashboard/settings/config/categories",
    icon: Tags,
  },
  // The fixed five-point scale skills and competencies are rated against
  // (WI-103 renamed this from "Levels" to free that word for the career ladder).
  // The route keeps its `/levels` path so existing links and the
  // /settings/levels redirect keep working — only the label changed.
  {
    label: "Performance Ratings",
    href: "/dashboard/settings/config/levels",
    icon: ListOrdered,
  },
  // Career ladder (WI-102) — a separate config from the ratings above.
  { label: "Role Levels", href: "/dashboard/settings/config/role-levels", icon: Layers },
  // Employment types + the weekly hours each one expects (EPIC-028).
  {
    label: "Employment Types",
    href: "/dashboard/settings/config/employment-types",
    icon: BriefcaseBusiness,
  },
  // Working-style profiles (Builder / Connector / Craftsperson). The table was
  // seeded from NOS and read-only until this page existed.
  {
    label: "Employee Profiles",
    href: "/dashboard/settings/config/employee-profiles",
    icon: UserSquare,
  },
  // Org-level classification (IC / Manager / Leadership / Officer). Carries a
  // NOS name per row, because NOS matches its own lookup on the name.
  {
    label: "Role Classifications",
    href: "/dashboard/settings/config/role-classifications",
    icon: UsersRound,
  },
  // Coarse work-location bucket. Never mirrored to NOS.
  { label: "Work Locations", href: "/dashboard/settings/config/work-locations", icon: Globe },
  {
    label: "Change Reasons",
    href: "/dashboard/settings/config/change-reasons",
    icon: ArrowLeftRight,
  },
  // Staffing constraints (EPIC-043). Two lists, kept adjacent because they only
  // make sense together: a constraint is rated on the scale beside it. The
  // ratings here are NOT the performance ratings above — that separation is the
  // point of the feature.
  //
  // There used to be three. "Constraint types" merged into "Constraints" when
  // the values moved to NOS: a grouping with a read-only value list under it did
  // not earn a page of its own.
  {
    label: "Constraints",
    href: "/dashboard/settings/config/constraints",
    icon: SlidersHorizontal,
  },
  {
    label: "Constraint Ratings",
    href: "/dashboard/settings/config/constraint-ratings",
    icon: Gauge,
  },
  // Sections that are built but not yet switched on. Last, because it is about
  // the app rather than about a reference list.
  {
    label: "Feature Availability",
    href: "/dashboard/settings/config/feature-availability",
    icon: Gem,
  },
  // Also about the app rather than a reference list, and next to the switch it
  // most often accompanies: turn a feature on for testing, divert its email.
  {
    label: "Email testing",
    href: "/dashboard/settings/config/email-testing",
    icon: MailWarning,
  },
  // The review emails' wording and on/off switches. Beside Email testing, the
  // other page about what the app sends.
  {
    label: "Notification Templates",
    href: "/dashboard/settings/config/notification-templates",
    icon: Bell,
  },
];

/**
 * The review reference lists, shown only while Reviews is switched on — one
 * tab holding four lists, rather than four tabs for a feature that is off.
 *
 * Labelled "Reviews", NOT "Feedback", even though the nav section this belongs
 * to is called Feedback: Settings already has a top-level **Feedback** tab for
 * the bug-and-feature widget, and the two would have sat on screen together
 * with identical labels and unrelated contents.
 */
const reviewsTab: SettingsSubNavTab = {
  label: "Reviews",
  href: "/dashboard/settings/config/reviews",
  icon: MessagesSquare,
};

/**
 * The company's tenets — what an endorsement on a review says somebody
 * embodies. Its own tab rather than a fifth list under "Reviews": these are
 * the company's values, which people will want to find by that name, and they
 * are the one review list a non-admin ever reads.
 *
 * Shown with the reviews tab, because nothing else uses them yet.
 */
const tenetsTab: SettingsSubNavTab = {
  label: "Company Tenets",
  href: "/dashboard/settings/config/company-tenets",
  icon: HeartHandshake,
};

/**
 * The management capability set (WI-311) — what a direct report rates their
 * manager on. Beside the reviews tabs because it is only ever read by the
 * review forms, and gated the same way.
 */
const managementCapabilitiesTab: SettingsSubNavTab = {
  label: "Management Capabilities",
  href: "/dashboard/settings/config/management-capabilities",
  icon: UserCog,
};

/**
 * Settings → Config: every admin-configurable reference list in one place, so
 * all reference data is editable without hunting through tabs.
 */
export function SettingsConfigLayout({ children }: { children: React.ReactNode }) {
  const { data: features } = useAppSettings();
  // Inserted before the two email pages (Email testing, Notification
  // Templates), which stay last — they are about the app rather than about a
  // reference list.
  const tabs =
    features?.reviewsEnabled === true
      ? [
          ...configTabs.slice(0, -2),
          reviewsTab,
          tenetsTab,
          managementCapabilitiesTab,
          ...configTabs.slice(-2),
        ]
      : configTabs;
  return (
    <SettingsSubNav tabs={tabs} section="Config">
      {children}
    </SettingsSubNav>
  );
}
